import {useCallback, useState} from "react";
import {ApiError} from "services/http/ApiError";
import {handleUnauthorized} from "services/http/authInterceptor";
import type {
  ArgsAllowed,
  ConfigOrPath,
  Method,
  unknownRecord,
} from "services/http/types";
import {
  createCompleteURL,
  isFormData,
  replacePathParams,
} from "services/http/utils";
import {getSessionStorageItem} from "utils/storage";

export const createDownloadRequest = <Args extends ArgsAllowed = void>(
  method: Method,
  configOrPath: ConfigOrPath,
) => {
  const baseConfig =
    typeof configOrPath === "string"
      ? {path: configOrPath, method}
      : {...configOrPath, method};

  return () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);
    const [downloadStarted, setDownloadStarted] = useState(false);

    const download = useCallback(
      async (
        args: Args,
        options?: {queryParams?: unknownRecord; filename?: string},
      ) => {
        setLoading(true);
        setError(null);
        setDownloadStarted(false);

        try {
          let path: string;
          let httpParams: unknownRecord | FormData | undefined;

          if (isFormData(args)) {
            path = baseConfig.path;
            httpParams = args;
          } else {
            const [p, otherArgs] = replacePathParams(
              baseConfig.path,
              (args ?? {}) as unknownRecord,
            );
            path = p;
            httpParams = otherArgs;
          }

          const BODY_METHODS = new Set<Method>([
            "POST",
            "PUT",
            "PATCH",
            "DELETE",
          ]);
          const queryParams = BODY_METHODS.has(method)
            ? options?.queryParams
            : (httpParams as unknownRecord | undefined);

          const url = createCompleteURL(path, queryParams);

          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };

          try {
            const stored = getSessionStorageItem<{token?: string}>("authUser");
            const token = stored?.token;
            if (token) {
              headers["Authorization"] = `Bearer ${token}`;
            }
          } catch {
            // ignore
          }

          let body: BodyInit | undefined;
          if (BODY_METHODS.has(method)) {
            if (isFormData(httpParams)) {
              body = httpParams as FormData;
              delete headers["Content-Type"];
            } else if (httpParams !== undefined) {
              body = JSON.stringify(httpParams);
            }
          }

          const res = await fetch(url, {
            method,
            credentials: "include",
            cache: "no-cache",
            redirect: "follow",
            headers,
            body,
          });

          if (res.ok) {
            const contentDisposition =
              res.headers.get("content-disposition") || "";

            const isDownload = contentDisposition
              .toLowerCase()
              .includes("attachment");

            if (isDownload) {
              let filename = options?.filename || "download";

              if (contentDisposition) {
                const match = contentDisposition.match(
                  /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
                );
                if (match?.[1]) {
                  filename = match[1].replace(/['"]/g, "");
                }
              }

              const blob = await res.blob();
              const downloadUrl = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = downloadUrl;
              link.download = filename;
              link.style.display = "none";
              document.body.appendChild(link);
              link.click();

              setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
              }, 100);

              setDownloadStarted(true);
              return;
            }

            // If not a download, something went wrong
            throw new ApiError(
              500,
              "Expected downloadable content but got regular response",
            );
          }

          const errorData = await res
            .json()
            .then(data => ("errors" in data ? data.errors : data))
            .catch(() => undefined);

          if (res.status === 401 && errorData?.message === "Invalid Token") {
            handleUnauthorized();
          }

          throw new ApiError(
            res.status,
            errorData?.message || res.statusText,
            errorData,
          );
        } catch (err) {
          setError(err as ApiError);
          throw err;
        } finally {
          setLoading(false);
        }
      },
      [baseConfig.path, method],
    );

    return {download, loading, error, downloadStarted} as const;
  };
};
